"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MedicineDetailsDialog } from "@/entities/medicine/ui/medicine-details-dialog";
import type { OrganizationMetric, OrganizationMetricMedicine, OrganizationMetricSortBy, OrganizationMetricSortDir } from "@/entities/organization/api/organization-metric-drilldown";
import { useOrganizationMetricDrilldownQuery } from "@/entities/organization/model/use-organization-metric-drilldown";
import { buildApiErrorMessage } from "@/shared/api/common";
import type { OrganizationRegion } from "@/shared/config/constants";
import { useIsMobile } from "@/shared/hooks/use-mobile";
import { toIntlLocale } from "@/shared/lib/format/locale";
import { formatCount } from "@/shared/lib/format/numbers";
import { cn } from "@/shared/lib/utils";
import { EmptyState } from "@/shared/ui/empty-state";
import { Input } from "@/shared/ui/input";
import { ListTable } from "@/shared/ui/list-table";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { ServerPagination } from "@/shared/ui/server-pagination";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/shared/ui/sheet";
import type { OrganizationMetricDrilldownTarget } from "./organization-metric-chip";
import { buildDrilldownColumns } from "./organization-metric-drilldown-columns";
const DEFAULT_PAGE_SIZE = 100;
const DEFAULT_SORT: Record<OrganizationMetric, OrganizationMetricSortBy> = {
  exclusive: "matched_days",
  cheapest: "matched_days",
  promotion: "matched_offers"
};
export function OrganizationMetricDrilldownSheet(t0) {
  const $ = _c(15);
  const {
    target,
    region,
    paymentPercentage: t1,
    onOpenChange
  } = t0;
  const paymentPercentage = t1 === undefined ? 100 : t1;
  const isMobile = useIsMobile();
  const t2 = target != null;
  const t3 = isMobile ? "bottom" : "right";
  const t4 = isMobile ? "data-[side=bottom]:h-[88vh] data-[side=bottom]:max-h-[88vh] data-[side=bottom]:rounded-t-xl" : "data-[side=right]:w-[60rem]! sm:max-w-[60rem]!";
  let t5;
  if ($[0] !== t4) {
    t5 = cn("w-full gap-0 overflow-hidden p-0", t4);
    $[0] = t4;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== isMobile || $[3] !== paymentPercentage || $[4] !== region || $[5] !== target) {
    t6 = target ? <DrilldownContent key={`${target.organizationId}:${target.metric}`} target={target} region={region} paymentPercentage={paymentPercentage} isMobile={isMobile} /> : null;
    $[2] = isMobile;
    $[3] = paymentPercentage;
    $[4] = region;
    $[5] = target;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t3 || $[8] !== t5 || $[9] !== t6) {
    t7 = <SheetContent side={t3} data-testid="organization-metric-drilldown-sheet" className={t5}>{t6}</SheetContent>;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== onOpenChange || $[12] !== t2 || $[13] !== t7) {
    t8 = <Sheet open={t2} onOpenChange={onOpenChange}>{t7}</Sheet>;
    $[11] = onOpenChange;
    $[12] = t2;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
}
function DrilldownContent(t0) {
  const $ = _c(107);
  const {
    target,
    region,
    paymentPercentage,
    isMobile
  } = t0;
  const {
    t,
    i18n
  } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState(DEFAULT_SORT[target.metric]);
  const [sortDir, setSortDir] = useState("desc");
  const [detailsMedicineId, setDetailsMedicineId] = useState(null);
  let t1;
  if ($[0] !== page || $[1] !== pageSize || $[2] !== paymentPercentage || $[3] !== region || $[4] !== search || $[5] !== sortBy || $[6] !== sortDir || $[7] !== target.metric || $[8] !== target.organizationId) {
    t1 = {
      organizationId: target.organizationId,
      metric: target.metric,
      region,
      paymentPercentage,
      search,
      sortBy,
      sortDir,
      page,
      limit: pageSize
    };
    $[0] = page;
    $[1] = pageSize;
    $[2] = paymentPercentage;
    $[3] = region;
    $[4] = search;
    $[5] = sortBy;
    $[6] = sortDir;
    $[7] = target.metric;
    $[8] = target.organizationId;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const query = useOrganizationMetricDrilldownQuery(t1);
  const data = query.data?.data;
  let t2;
  if ($[10] !== data) {
    t2 = data ?? [];
    $[10] = data;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== search || $[13] !== t2) {
    t3 = filterMedicines(t2, search);
    $[12] = search;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const filteredData = t3;
  const summary = query.data?.summary;
  const pagination = query.data?.pagination;
  const days = summary?.window_days ?? 90;
  const totalPages = pagination?.totalPages ?? 0;
  let t4;
  if ($[15] !== i18n.language) {
    t4 = toIntlLocale(i18n.language);
    $[15] = i18n.language;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const intlLocale = t4;
  let t5;
  if ($[17] !== sortBy) {
    t5 = field => {
      if (field === sortBy) {
        setSortDir(_temp);
      } else {
        setSortBy(field);
        setSortDir(field === "medicine_name" ? "asc" : "desc");
      }
      setPage(1);
    };
    $[17] = sortBy;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const onSort = t5;
  let t6;
  if ($[19] !== intlLocale || $[20] !== isMobile || $[21] !== onSort || $[22] !== sortBy || $[23] !== sortDir || $[24] !== t || $[25] !== target.metric) {
    t6 = buildDrilldownColumns({
      metric: target.metric,
      intlLocale,
      isMobile,
      sortBy,
      sortDir,
      onSort,
      t
    });
    $[19] = intlLocale;
    $[20] = isMobile;
    $[21] = onSort;
    $[22] = sortBy;
    $[23] = sortDir;
    $[24] = t;
    $[25] = target.metric;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const columns = t6;
  const t7 = `organizations.ratings.drilldown.description.${target.metric}`;
  let t8;
  if ($[27] !== days || $[28] !== t || $[29] !== t7) {
    t8 = t(t7, {
      days
    });
    $[27] = days;
    $[28] = t;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  const description = t8;
  let t9;
  if ($[31] !== query.error || $[32] !== query.isError) {
    t9 = query.isError ? buildApiErrorMessage(query.error, "Unable to load ranking medicines.") : null;
    $[31] = query.error;
    $[32] = query.isError;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  const errorMessage = t9;
  const t10 = query.data?.organization?.name ?? target.organizationName;
  let t11;
  if ($[34] !== t10) {
    t11 = <SheetTitle className="text-base">{t10}</SheetTitle>;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  const t12 = target.expectedPctLabel ? ` · ${target.expectedPctLabel}` : "";
  let t13;
  if ($[36] !== description || $[37] !== t12) {
    t13 = <SheetDescription>{description}{t12}</SheetDescription>;
    $[36] = description;
    $[37] = t12;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t11 || $[40] !== t13) {
    t14 = <SheetHeader className="border-b px-6 pb-3"><div className="pr-10">{t11}{t13}</div></SheetHeader>;
    $[39] = t11;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== t) {
    t15 = t("organizations.ratings.drilldown.summary.matches");
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  const t16 = summary?.metric_total ?? target.expectedCount;
  let t17;
  if ($[44] !== t16) {
    t17 = formatCount(t16);
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t15 || $[47] !== t17) {
    t18 = <SummaryItem label={t15} value={t17} />;
    $[46] = t15;
    $[47] = t17;
    $[48] = t18;
  } else {
    t18 = $[48];
  }
  let t19;
  if ($[49] !== t) {
    t19 = t("organizations.ratings.drilldown.summary.medicines");
    $[49] = t;
    $[50] = t19;
  } else {
    t19 = $[50];
  }
  const t20 = summary?.matched_medicines ?? pagination?.total;
  let t21;
  if ($[51] !== t20) {
    t21 = formatCount(t20);
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== t19 || $[54] !== t21) {
    t22 = <SummaryItem label={t19} value={t21} />;
    $[53] = t19;
    $[54] = t21;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] !== t) {
    t23 = t("organizations.ratings.drilldown.summary.window");
    $[56] = t;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  let t24;
  if ($[58] !== days || $[59] !== t) {
    t24 = t("organizations.ratings.drilldown.daysWindow", {
      count: days
    });
    $[58] = days;
    $[59] = t;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t23 || $[62] !== t24) {
    t25 = <SummaryItem label={t23} value={t24} />;
    $[61] = t23;
    $[62] = t24;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t18 || $[65] !== t22 || $[66] !== t25) {
    t26 = <dl className="grid grid-cols-3 gap-3 border-b px-6 py-3">{t18}{t22}{t25}</dl>;
    $[64] = t18;
    $[65] = t22;
    $[66] = t25;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  let t27;
  if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = event => {
      setSearch(event.target.value);
      setPage(1);
    };
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t) {
    t29 = t("organizations.ratings.drilldown.searchPlaceholder");
    $[70] = t;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== t) {
    t30 = t("organizations.ratings.drilldown.searchPlaceholder");
    $[72] = t;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  let t31;
  if ($[74] !== search || $[75] !== t29 || $[76] !== t30) {
    t31 = <Input value={search} onChange={t28} placeholder={t29} aria-label={t30} className="bg-background h-9 pr-9 pl-9" />;
    $[74] = search;
    $[75] = t29;
    $[76] = t30;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== search || $[79] !== t) {
    t32 = search ? <button type="button" onClick={() => setSearch("")} aria-label={t("organizations.ratings.drilldown.clearSearch")} className="text-muted-foreground hover:text-foreground hover:bg-muted absolute top-1/2 right-2 -translate-y-1/2 cursor-pointer rounded-full p-0.5"><X className="size-3.5" aria-hidden={true} /></button> : null;
    $[78] = search;
    $[79] = t;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t31 || $[82] !== t32) {
    t33 = <div className="border-b px-6 py-3"><div className="relative">{t27}{t31}{t32}</div></div>;
    $[81] = t31;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== columns || $[85] !== errorMessage || $[86] !== filteredData || $[87] !== query.isLoading || $[88] !== t || $[89] !== target.metric) {
    t34 = <ScrollArea className="min-h-0 flex-1">{errorMessage ? <EmptyState title={t("organizations.ratings.drilldown.errorTitle")} description={errorMessage} /> : !query.isLoading && filteredData.length === 0 ? <EmptyState title={t("organizations.ratings.drilldown.emptyTitle")} description={t(`organizations.ratings.drilldown.emptyDescription.${target.metric}`)} /> : <ListTable columns={columns} data={filteredData} getRowId={_temp2} loading={query.isLoading} skeletonRowCount={8} wrapperClassName={false} tableClassName="table-fixed [&_td]:px-3 [&_th]:px-3 [&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6" headerRowClassName="bg-background sticky top-0 z-10 shadow-[inset_0_-1px_0_var(--border)]" onRowClick={row_0 => setDetailsMedicineId(row_0.medicine?.id ?? row_0.medicine_id)} />}</ScrollArea>;
    $[84] = columns;
    $[85] = errorMessage;
    $[86] = filteredData;
    $[87] = query.isLoading;
    $[88] = t;
    $[89] = target.metric;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  let t35;
  if ($[91] !== page || $[92] !== pageSize || $[93] !== pagination?.total || $[94] !== totalPages) {
    t35 = (pagination?.total ?? 0) > 20 ? <SheetFooter className="border-t px-4 py-2"><ServerPagination currentPage={page} totalPages={totalPages} pageSize={pageSize} pageSizeOptions={[20, 50, 100]} onPageChange={setPage} onPageSizeChange={size => {
        setPageSize(size);
        setPage(1);
      }} /></SheetFooter> : null;
    $[91] = page;
    $[92] = pageSize;
    $[93] = pagination?.total;
    $[94] = totalPages;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  const t36 = detailsMedicineId != null;
  let t37;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = open => {
      if (!open) {
        setDetailsMedicineId(null);
      }
    };
    $[96] = t37;
  } else {
    t37 = $[96];
  }
  let t38;
  if ($[97] !== detailsMedicineId || $[98] !== t36) {
    t38 = <MedicineDetailsDialog medicineId={detailsMedicineId} open={t36} onOpenChange={t37} />;
    $[97] = detailsMedicineId;
    $[98] = t36;
    $[99] = t38;
  } else {
    t38 = $[99];
  }
  let t39;
  if ($[100] !== t14 || $[101] !== t26 || $[102] !== t33 || $[103] !== t34 || $[104] !== t35 || $[105] !== t38) {
    t39 = <>{t14}{t26}{t33}{t34}{t35}{t38}</>;
    $[100] = t14;
    $[101] = t26;
    $[102] = t33;
    $[103] = t34;
    $[104] = t35;
    $[105] = t38;
    $[106] = t39;
  } else {
    t39 = $[106];
  }
  return t39;
}
function _temp2(row) {
  return row.medicine_id;
}
function _temp(current) {
  return current === "desc" ? "asc" : "desc";
}
function filterMedicines(data: OrganizationMetricMedicine[], search: string) {
  const query = search.trim().toLocaleLowerCase();
  if (!query) return data;
  return data.filter(row => [row.raw_name, row.medicine?.name, row.medicine?.trade_name, row.medicine?.international_name, row.medicine?.dosage_form, row.medicine?.strength, row.medicine?.volume, row.medicine?.pack == null ? null : String(row.medicine.pack)].some(value => value?.toLocaleLowerCase().includes(query)));
}
function SummaryItem(t0) {
  const $ = _c(7);
  const {
    label,
    value
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <dt className="text-muted-foreground text-[10px] tracking-wide uppercase">{label}</dt>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== value) {
    t2 = <dd className="text-foreground mt-0.5 text-base font-semibold tabular-nums">{value}</dd>;
    $[2] = value;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}